import React, { useState, useEffect } from 'react';
import { 
  BarChart, Activity, TrendingUp, Clock, 
  AlertCircle, Users, Navigation, Settings,
  Download, Trash2, Eye, EyeOff
} from 'lucide-react';
import { analytics } from '../utils/analytics';

export const AnalyticsDashboard: React.FC = () => {
  const [insights, setInsights] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [enabled, setEnabled] = useState(false);
  const [showRawData, setShowRawData] = useState(false);

  useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadAnalytics, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadAnalytics = () => {
    const currentInsights = analytics.getInsights();
    const currentSummary = analytics.getSummary();
    setInsights(currentInsights);
    setSummary(currentSummary);
    setEnabled(localStorage.getItem('analytics_opt_in') === 'true');
  };

  const toggleAnalytics = () => {
    const newEnabled = !enabled;
    analytics.setEnabled(newEnabled);
    setEnabled(newEnabled);
    loadAnalytics();
  };

  const exportData = () => {
    const data = analytics.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearData = () => {
    if (confirm('Clear all analytics data? This cannot be undone.')) {
      analytics.clearData();
      loadAnalytics();
    }
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-purple-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
              <p className="text-gray-400">Privacy-first usage insights</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleAnalytics}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  enabled 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {enabled ? 'Tracking On' : 'Tracking Off'}
              </button>
              <button
                onClick={exportData}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Export Data"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={clearData}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-red-400"
                title="Clear Data"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-400">Privacy First</h3>
                <p className="text-sm text-gray-300 mt-1">
                  All analytics data is stored locally on your device. No data is sent to external servers 
                  unless you explicitly export it. You have full control over your data.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Current Session */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Session Duration</span>
              </div>
              <p className="text-2xl font-bold">
                {formatDuration(summary.endTime - summary.startTime)}
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Activity className="w-5 h-5 text-green-400" />
                <span className="text-sm text-gray-400">Events</span>
              </div>
              <p className="text-2xl font-bold">{summary.events}</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Navigation className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-gray-400">Pages Visited</span>
              </div>
              <p className="text-2xl font-bold">{summary.pages.length}</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-sm text-gray-400">Errors</span>
              </div>
              <p className="text-2xl font-bold">{summary.errors}</p>
            </div>
          </div>
        )}

        {/* Insights */}
        {insights && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Most Visited Pages */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <BarChart className="w-5 h-5 text-purple-400" />
                Most Visited Pages
              </h2>
              <div className="space-y-3">
                {insights.mostVisitedPages.map(([page, count]: [string, number], i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm">{page}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-white/10 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full"
                          style={{ 
                            width: `${(count / insights.mostVisitedPages[0][1]) * 100}%` 
                          }}
                        />
                      </div>
                      <span className="text-sm text-gray-400 w-10 text-right">{count}</span>
                    </div>
                  </div>
                ))}
                {insights.mostVisitedPages.length === 0 && (
                  <p className="text-gray-400 text-sm">No page visits recorded yet</p>
                )}
              </div>
            </div>

            {/* Feature Usage */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Feature Usage
              </h2>
              <div className="space-y-3">
                {insights.featureUsage.slice(0, 5).map(([feature, count]: [string, number], i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm">{feature.replace(/_/g, ' ')}</span>
                    <span className="text-sm text-gray-400">{count} times</span>
                  </div>
                ))}
                {insights.featureUsage.length === 0 && (
                  <p className="text-gray-400 text-sm">No feature usage recorded yet</p>
                )}
              </div>
            </div>

            {/* Common User Flows */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-400" />
                Common User Flows
              </h2>
              <div className="space-y-3">
                {insights.commonFlows.slice(0, 3).map((flow: string[], i: number) => (
                  <div key={i} className="text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      {flow.map((page, j) => (
                        <React.Fragment key={j}>
                          <span className="px-2 py-1 bg-white/10 rounded">{page}</span>
                          {j < flow.length - 1 && <span className="text-gray-500">→</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
                {insights.commonFlows.length === 0 && (
                  <p className="text-gray-400 text-sm">No user flows recorded yet</p>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-yellow-400" />
                Statistics
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-400">Total Events</span>
                  <span className="text-sm font-semibold">{insights.totalEvents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-400">Unique Sessions</span>
                  <span className="text-sm font-semibold">{insights.uniqueSessions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-400">Avg Session Length</span>
                  <span className="text-sm font-semibold">
                    {insights.averageSessionLength?.toFixed(1) || 0} min
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-400">Recent Errors</span>
                  <span className="text-sm font-semibold">{insights.recentErrors?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Raw Data Viewer */}
        <div className="mt-8">
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
          >
            <Settings className="w-4 h-4" />
            {showRawData ? 'Hide' : 'Show'} Raw Data
          </button>
          
          {showRawData && (
            <div className="mt-4 bg-black/30 rounded-lg p-4 overflow-x-auto">
              <pre className="text-xs text-gray-300">
                {JSON.stringify({ summary, insights }, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* AI Insights (Future) */}
        <div className="mt-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            AI Insights (Coming Soon)
          </h2>
          <p className="text-gray-300 mb-4">
            Once enough data is collected, AI will analyze your usage patterns and provide:
          </p>
          <ul className="space-y-2 text-sm text-gray-400">
            <li>• Personalized workflow optimizations</li>
            <li>• Predictive feature suggestions</li>
            <li>• Automated UI improvements</li>
            <li>• Usage pattern anomaly detection</li>
            <li>• Performance bottleneck identification</li>
          </ul>
        </div>
      </div>
    </div>
  );
};